import { Alert, Button, Form, Input, Space, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { SuperAdminContext, SuperAdminScope } from 'src/components';
import { BackIconMobile } from 'src/icons';
import { usePageHeader } from 'src/hooks/misc';
import { useSuperAdminApiOverrides } from 'src/hooks/superAdmin';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';
import { getApiErrorMessage, magicFlowApi } from 'src/services';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import OrganizationFilter from '../OrganizationFilter';
import { WorkflowTemplateBuilder } from './components/WorkflowTemplateBuilder';
import type {
  Option,
  WorkflowActionSchema,
  WorkflowDecisionSchema,
} from './components/WorkflowTemplateBuilder.types';

const OrganizationFilterBase = OrganizationFilter as any;

type WorkflowTemplateRecord = {
  workflow_template_id: string;
  name: string;
  description?: string;
  workflow: Record<string, unknown>;
};

type WorkflowTemplateFormValues = {
  name: string;
  description?: string;
  workflow: Record<string, any>;
};

const DEFAULT_WORKFLOW_TEMPLATE = {
  workflow_type: 'single_step',
  required_inputs: [],
  steps: [
    {
      id: 'step_1',
      name: 'Step 1',
      type: 'action',
      action: {
        type: 'echo',
        inputs: {
          message: 'Hello world',
        },
      },
    },
  ],
};

const normalizeWorkflowTemplateValue = (
  workflow: Record<string, unknown> | undefined
): Record<string, any> => {
  const nextWorkflow = (workflow || {}) as Record<string, any>;
  const steps = Array.isArray(nextWorkflow.steps) ? nextWorkflow.steps : [];

  return {
    workflow_type: nextWorkflow.workflow_type || 'single_step',
    required_inputs: Array.isArray(nextWorkflow.required_inputs)
      ? nextWorkflow.required_inputs
      : [],
    steps: steps.length > 0 ? steps : DEFAULT_WORKFLOW_TEMPLATE.steps,
  };
};

const MagicFlowTemplateEditorContent = () => {
  const dispatch = useDispatch<any>();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId?: string }>();
  const isEditMode = Boolean(templateId && templateId !== 'new');

  const { org } = useContext(SuperAdminContext);
  const orgUuid = org?.org_uuid;
  const hasOrgSelected = Boolean(orgUuid);
  const { headers: superAdminHeaders } = useSuperAdminApiOverrides();
  const [form] = Form.useForm<WorkflowTemplateFormValues>();

  const scopedHeaders = useMemo(() => {
    return {
      ...(superAdminHeaders || {}),
    } as Record<string, string | boolean>;
  }, [superAdminHeaders]);

  const schemaHeaders = useMemo(() => {
    const nextHeaders = {
      ...(superAdminHeaders || {}),
    } as Record<string, string | boolean>;
    delete nextHeaders['Impersonate-Org-UUID'];
    delete nextHeaders['Headless-Mode'];
    return nextHeaders;
  }, [superAdminHeaders]);

  usePageHeader({
    title: isEditMode ? 'Edit Workflow Template' : 'Create Workflow Template',
  });

  useEffect(() => {
    dispatch(getAllActiveOrganizationAction());
  }, [dispatch]);

  const { data: schema } = useQuery({
    queryKey: ['magicflow-schema', orgUuid],
    enabled: hasOrgSelected,
    queryFn: () =>
      magicFlowApi
        .get<{
          workflow_actions?: Record<string, WorkflowActionSchema>;
          workflow_decisions?: Record<string, WorkflowDecisionSchema>;
          conditions?: Record<string, { description?: string }>;
        }>('/schema', {
          headers: schemaHeaders,
        })
        .then((res) => res.data),
  });

  const { data: existingTemplate, isLoading: isLoadingTemplate } = useQuery({
    queryKey: ['magicflow-workflow-template-detail', orgUuid, templateId],
    enabled: hasOrgSelected && isEditMode,
    queryFn: () =>
      magicFlowApi
        .get<{
          workflow_template: WorkflowTemplateRecord;
        }>(`/workflow_template/${templateId}`, { headers: scopedHeaders })
        .then((res) => res.data.workflow_template),
  });

  useEffect(() => {
    if (isEditMode) {
      if (!existingTemplate) return;
      form.setFieldsValue({
        name: existingTemplate.name,
        description: existingTemplate.description || '',
        workflow: normalizeWorkflowTemplateValue(existingTemplate.workflow),
      });
      return;
    }

    form.setFieldsValue({
      name: '',
      description: '',
      workflow: normalizeWorkflowTemplateValue(DEFAULT_WORKFLOW_TEMPLATE),
    });
  }, [existingTemplate, form, isEditMode]);

  const workflowActionSchemas = useMemo(
    () => schema?.workflow_actions || {},
    [schema?.workflow_actions]
  );

  const workflowDecisionSchemas = useMemo(
    () => schema?.workflow_decisions || {},
    [schema?.workflow_decisions]
  );

  const conditionOptions = useMemo<Option[]>(() => {
    const operators = schema?.conditions || {};
    return Object.keys(operators)
      .sort((a, b) => a.localeCompare(b))
      .map((operator) => ({
        value: operator,
        label: operator,
      }));
  }, [schema?.conditions]);

  const saveTemplateMutation = useMutation({
    mutationFn: async (payload: {
      name: string;
      description?: string;
      workflow: Record<string, unknown>;
    }) => {
      if (isEditMode) {
        return magicFlowApi.put(`/workflow_template/${templateId}`, payload, {
          headers: scopedHeaders,
        });
      }
      return magicFlowApi.post('/workflow_template', payload, {
        headers: scopedHeaders,
      });
    },
    onSuccess: () => {
      showToastSuccess(
        isEditMode
          ? 'Workflow template updated successfully'
          : 'Workflow template created successfully'
      );
      queryClient.invalidateQueries({
        queryKey: ['magicflow-workflow-templates', orgUuid],
      });
      navigate('/superadmin/magicflow');
    },
    onError: (error) => {
      showToastError(
        getApiErrorMessage(error as any) || 'Failed to save workflow template'
      );
    },
  });

  const handleSave = async () => {
    if (!hasOrgSelected) {
      showToastError('Select an organization before saving the template.');
      return;
    }

    const values = await form.validateFields();
    const normalizedWorkflow = normalizeWorkflowTemplateValue(
      values.workflow as Record<string, unknown>
    );
    saveTemplateMutation.mutate({
      name: values.name,
      description: values.description,
      workflow: normalizedWorkflow,
    });
  };

  const topFormItemLayout = {
    labelCol: { flex: '140px' },
    wrapperCol: { flex: 'auto' },
  };

  return (
    <div className="page-wrapper">
      <div className="flex justify-between items-center gap-2 mb-2">
        <div className="flex items-center gap-2">
          <BackIconMobile
            className=""
            onClick={() => navigate('/superadmin/magicflow/templates')}
          />
          <Typography.Text className="font-medium">
            Workflow Templates
          </Typography.Text>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-md text-gray-600 font-medium">
            Select organization:
          </label>
          <OrganizationFilterBase />
        </div>
      </div>

      {!hasOrgSelected && (
        <Alert
          className="mt-3"
          type="info"
          showIcon
          message="Select an organization to edit workflow templates."
        />
      )}

      <div className="mt-3">
        <Form form={form} layout="horizontal" labelAlign="left">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
            {...topFormItemLayout}
          >
            <Input placeholder="Workflow template name" />
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            {...topFormItemLayout}
          >
            <Input.TextArea rows={3} placeholder="Description (optional)" />
          </Form.Item>

          <WorkflowTemplateBuilder
            workflowActionSchemas={workflowActionSchemas}
            workflowDecisionSchemas={workflowDecisionSchemas}
            conditionOptions={conditionOptions}
          />
        </Form>
      </div>

      <div className="mt-4 flex justify-end">
        <Space>
          <Button onClick={() => navigate('/superadmin/magicflow/templates')}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={saveTemplateMutation.isPending}
            onClick={handleSave}
            disabled={!hasOrgSelected || isLoadingTemplate}
          >
            Save Template
          </Button>
        </Space>
      </div>
    </div>
  );
};

const MagicFlowTemplateEditorPage = () => (
  <SuperAdminScope>
    <MagicFlowTemplateEditorContent />
  </SuperAdminScope>
);

export default MagicFlowTemplateEditorPage;
